import type { AppState } from '../../state/appState.js';
import type { SceneNodeId } from '../../scene/sceneState.js';
import type { ActionExecution, EditorAction } from '../commandExecutor.js';
import type { EditorSessionState } from './session.js';
import {
  cloneComponentDef,
  cloneLayerDef,
  cloneLibraryItem,
  cloneSceneNodeMap,
  collectSceneSubtree,
} from './shared.js';
import { captureEditorSessionFor } from './session.js';

export interface ClearHooks {
  captureEditorSession?: () => EditorSessionState;
}

export function execClearFor(appState: AppState, hooks: ClearHooks = {}): ActionExecution<void> {
  const session = hooks.captureEditorSession?.() ?? captureEditorSessionFor(appState);
  const sceneRootIds = [...appState.scene.rootIds];
  const sceneSubtrees = sceneRootIds.map((rootId, index) => ({
    rootId,
    targetComponentId: null as string | null,
    parentId: null as SceneNodeId | null,
    index,
    nodes: collectSceneSubtree(appState.scene, rootId),
  }));
  const components = Object.values(appState.componentLibrary).map(component => cloneComponentDef(component));
  const layerDefs = Object.values(appState.layerLibrary).map(layer => cloneLayerDef(layer));
  const geometryItems = Object.values(appState.geometryLibrary.user).map(item => cloneLibraryItem(item));
  const materialItems = Object.values(appState.materialLibrary.user).map(item => cloneLibraryItem(item));
  const params = Object.entries(appState.params).map(([key, value]) => ({ key, value }));

  appState.nodes = [];
  appState.edges = [];
  appState.editorGraphDirty = false;
  appState.params = {};
  appState.selectedNode = null;
  appState.scene = { rootIds: [], nodes: {} };
  appState.componentLibrary = {};
  appState.layerLibrary = {};
  appState.geometryLibrary.user = {};
  appState.materialLibrary.user = {};
  appState.activeComponentId = null;
  appState.editorGraphSlot = 'geometry';
  appState.editorGraphMode = 'auto';
  appState.editorContext = {
    mode: 'layer',
    componentId: undefined,
    layerId: undefined,
    layerSlot: 'geometry',
  };

  return {
    result: undefined,
    undoActions: [
      ...geometryItems.map(item => ({ type: 'library:restore', kind: 'geometry', item }) as EditorAction),
      ...materialItems.map(item => ({ type: 'library:restore', kind: 'material', item }) as EditorAction),
      ...layerDefs.map(layerDef => ({ type: 'layer:restore', layerDef }) as EditorAction),
      ...components.map(component => ({ type: 'component:restore', component }) as EditorAction),
      ...sceneSubtrees.map(subtree => ({
        type: 'scene:restoreSubtree',
        targetComponentId: subtree.targetComponentId,
        rootId: subtree.rootId,
        parentId: subtree.parentId,
        index: subtree.index,
        nodes: cloneSceneNodeMap(subtree.nodes),
      }) as EditorAction),
      ...params.map(param => ({ type: 'params:setValue', key: param.key, value: param.value }) as EditorAction),
      { type: 'editor:restoreSession', session },
    ],
    changed: true,
  };
}
